import { PlusOutlined } from '@ant-design/icons'
import { Button, Flex, List, Popover, Table, Tag } from 'antd'
import { ColumnsType } from 'antd/es/table'
import dayjs from 'dayjs'
import { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { useStocksServiceGetReturnListEndpoint } from 'src/api/queries'
import {
  InventoryDocumentResponse,
  InventoryDocumentState,
  Permissions
} from 'src/api/requests'
import { PageBreadcrumbs } from 'src/components'
import { getEntryStatus, SelectedItemsState } from 'src/features/stock'
import { ReturnsFilters } from 'src/features/stock/components/returns'
import { useTablePagination, useUser } from 'src/hooks'
import { dayMonthYearBackSlash } from 'src/libs'
import { useLocales } from 'src/locales'
import { paths } from 'src/paths'

const VISIBLE_TAG_COUNT = 2

const MapValuesToIds = (data: SelectedItemsState) => {
  const { userData, hasPermission } = useUser()

  const currentLegalEntityId = userData?.currentLegalEntityId

  return {
    legalEntityIds:
      data?.legalEntityIds?.values ||
      hasPermission(Permissions.STOCKS_MENU_ACCESS_ISSUANCE)
        ? []
        : currentLegalEntityId
          ? [currentLegalEntityId]
          : [],
    fromDate: data?.inDate?.values?.[0],
    toDate: data?.inDate?.values?.[1],
    searchTerm: data?.search?.values?.[0],
    states: data?.states?.values as InventoryDocumentState[]
  }
}

export const StockReturnsPage = () => {
  const [selectedItems, setSelectedItems] = useState<SelectedItemsState>({})

  const { t } = useLocales()
  const navigate = useNavigate()

  const { hasPermission } = useUser()
  const pagination = useTablePagination()

  const queryParams = {
    pageNumber: pagination.current,
    pageSize: pagination.pageSize,
    ...MapValuesToIds(selectedItems)
  }

  const { data, isLoading } = useStocksServiceGetReturnListEndpoint(queryParams)

  const onCreateClick = () => {
    navigate(paths.stock.returns.create)
  }

  const columns = useMemo(
    (): ColumnsType<InventoryDocumentResponse> => [
      {
        key: 'documentNumber',
        title: t('common.invoice_number'),
        render: (_, { documentNumber }) => `#${documentNumber}`
      },
      {
        key: 'created date',
        title: t('stock_applications.request_date'),
        render: (_, { documentDate }) =>
          dayjs(documentDate).format(dayMonthYearBackSlash)
      },
      {
        key: 'fromInstitution',
        title: t('stock_applications.juridic_person'),
        dataIndex: ['beneficiary', 'name']
      },
      {
        key: 'products',
        title: t('stock_applications.consumables'),
        render: (_, { items }) => {
          if (!items?.length) return '-'

          const extraCount = items?.length - VISIBLE_TAG_COUNT

          return (
            <>
              <List
                itemLayout="horizontal"
                dataSource={items}
                renderItem={(item, index) =>
                  index < VISIBLE_TAG_COUNT && (
                    <Tag
                      key={item?.id}
                    >{`${item?.name} (${item?.quantity})`}</Tag>
                  )
                }
              />
              <Popover
                content={
                  <Flex vertical gap={4}>
                    {items?.slice(VISIBLE_TAG_COUNT).map((item) => (
                      <Tag
                        key={item?.id}
                      >{`${item?.name} (${item?.quantity})`}</Tag>
                    ))}
                  </Flex>
                }
              >
                {extraCount > 0 && <Tag className="pointer">+{extraCount}</Tag>}
              </Popover>
            </>
          )
        }
      },
      {
        key: 'status',
        title: t('form.status'),
        render: ({ state }) => {
          return (
            <Tag color={getEntryStatus(t, state).color} key={state}>
              {getEntryStatus(t, state).text}
            </Tag>
          )
        }
      },
      {
        key: 'details',
        render: (_, { id }) => (
          <Link to={paths.stock.returns.details(String(id))}>
            {t('common.details')}
          </Link>
        )
      }
    ],
    [t]
  )

  return (
    <PageBreadcrumbs
      title={t('stock.returns')}
      items={[
        { title: t('menu.consumable_medicaments') },
        { title: t('stock.returns') }
      ]}
    >
      <Flex vertical gap={16}>
        <Flex gap={16}>
          <ReturnsFilters
            selectedItems={selectedItems}
            setSelectedItems={setSelectedItems}
          />

          {hasPermission(Permissions.STOCKS_STOCK_CREATE_RETURNS) && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={onCreateClick}
            >
              {t('stock_returns.create_new_return')}
            </Button>
          )}
        </Flex>

        <Table
          rowKey="id"
          dataSource={data?.data}
          columns={columns}
          loading={isLoading}
          scroll={{ x: 'max-content' }}
          pagination={{ ...pagination, total: data?.totalCount }}
        />
      </Flex>
    </PageBreadcrumbs>
  )
}
